import {
  OktonLoadingCard,
  OktonErrorCard,
  OktonPageHeader,
  OktonSearchFilter,
  OktonConfirmDialog,
} from '@okton/platform-ui';
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Lock,
  Network,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BankAppPage from '../../layout/BankAppPage';
import {
  bloqueioPlanoDFC,
  montarArvore,
  NoPlanoDFC,
  useDeletePlanoDFC,
  usePlanoDFC,
} from '../../services/hooks/usePlanoDFC';

/**
 * Plano de Contas da DFC: a árvore que classifica o Fluxo de Caixa.
 *
 * Nível só agrupa; Conta é a folha que recebe lançamento — é ela que os
 * seletores de Plano de Contas das outras telas oferecem.
 *
 * A grade aqui é uma árvore e não uma lista: a numeração do item só faz sentido
 * junto do que está acima dela.
 */
const PlanoContas = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const { data: linhas = [], isLoading, isError, refetch } = usePlanoDFC();
  const deleteMutation = useDeletePlanoDFC();
  const toast = useToast();
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const [itemExcluir, setItemExcluir] = useState<NoPlanoDFC | null>(null);
  const [busca, setBusca] = useState('');
  const [fechados, setFechados] = useState<Record<string, boolean>>({});

  const arvore = useMemo(() => montarArvore(linhas), [linhas]);

  // Buscar mostra o que casa e os pais que levam até lá: um resultado sem o
  // caminho acima não diz onde ele fica.
  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (termo === '') return null;
    const encontrados = new Set<string>();
    const marcar = (no: NoPlanoDFC, ancestrais: string[]): boolean => {
      const casa =
        no.nome_pct.toLowerCase().includes(termo) ||
        no.item_pct.toLowerCase().includes(termo) ||
        (no.conta_pct ?? '').includes(termo);
      const filhosCasam = no.filhos
        .map(filho => marcar(filho, [...ancestrais, no.id_pct]))
        .some(Boolean);
      if (casa || filhosCasam) {
        encontrados.add(no.id_pct);
        ancestrais.forEach(id => encontrados.add(id));
        return true;
      }
      return false;
    };
    arvore.forEach(no => marcar(no, []));
    return encontrados;
  }, [arvore, busca]);

  // Recolher tudo marca cada nó que tem filhos; expandir é só esvaziar o
  // mapa, já que a ausência significa aberto.
  const recolherTudo = () => {
    const marcados: Record<string, boolean> = {};
    const percorre = (nos: NoPlanoDFC[]) => {
      nos.forEach(no => {
        if (no.filhos.length > 0) {
          marcados[no.id_pct] = true;
          percorre(no.filhos);
        }
      });
    };
    percorre(arvore);
    setFechados(marcados);
  };

  const tudoRecolhido =
    arvore.length > 0 && arvore.every(no => fechados[no.id_pct]);

  const excluir = async () => {
    if (!itemExcluir) return;
    setExcluindo(itemExcluir.id_pct);
    try {
      await deleteMutation.mutateAsync(itemExcluir.id_pct);
      setItemExcluir(null);
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : 'Erro ao excluir.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setExcluindo(null);
    }
  };

  const desenhaNo = (no: NoPlanoDFC): React.ReactNode => {
    if (visiveis && !visiveis.has(no.id_pct)) return null;
    const temFilhos = no.filhos.length > 0;
    const bloqueio = bloqueioPlanoDFC(no);
    // Durante a busca tudo fica aberto: fechar esconderia o resultado.
    const aberto = visiveis ? true : !fechados[no.id_pct];
    const ehConta = !!no.conta_pct;

    return (
      <Box key={no.id_pct}>
        <Flex
          align="center"
          gap="2"
          px="4"
          py="2"
          pl={`${16 + no.profundidade * 20}px`}
          borderBottomWidth="1px"
          _hover={{ bg: subtleBg }}
        >
          {temFilhos ? (
            <Icon
              as={aberto ? ChevronDown : ChevronRight}
              boxSize="4"
              color={mutedColor}
              flexShrink={0}
              cursor="pointer"
              onClick={() =>
                setFechados(atual => ({
                  ...atual,
                  [no.id_pct]: !atual[no.id_pct],
                }))
              }
            />
          ) : (
            <Box w="4" flexShrink={0} />
          )}

          <Text
            fontSize="xs"
            color={mutedColor}
            minW="88px"
            flexShrink={0}
            fontFamily="mono"
          >
            {no.item_pct}
          </Text>

          <Text
            fontSize="sm"
            color={color}
            fontWeight={ehConta ? 'normal' : 'semibold'}
            flex="1"
            minW="0"
            whiteSpace="normal"
            wordBreak="normal"
          >
            {no.nome_pct}
          </Text>

          {ehConta ? (
            <Badge colorScheme="blue" borderRadius="full" px="2" flexShrink={0}>
              Conta {no.conta_pct}
            </Badge>
          ) : (
            <Badge colorScheme="gray" borderRadius="full" px="2" flexShrink={0}>
              Nível
            </Badge>
          )}

          {/* Sem os botões quando o IFLEX não deixa mexer: mostrá-los
          desabilitados só convidaria a um clique que não faz nada. */}
          <Flex
            flexShrink={0}
            whiteSpace="nowrap"
            minW="72px"
            justify="flex-end"
          >
            {bloqueio === '' ? (
              <>
                <IconButton
                  aria-label="Editar"
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                  icon={<Pencil size={16} />}
                  onClick={() => history.push(`/plano-contas/${no.id_pct}`)}
                />
                <IconButton
                  aria-label="Excluir"
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  icon={<Trash2 size={16} />}
                  isLoading={excluindo === no.id_pct}
                  onClick={() => setItemExcluir(no)}
                />
              </>
            ) : (
              <Tooltip label={bloqueio} placement="left">
                <Box color="gray.300" px="2">
                  <Lock size={14} />
                </Box>
              </Tooltip>
            )}
          </Flex>
        </Flex>
        {temFilhos && aberto && <Box>{no.filhos.map(desenhaNo)}</Box>}
      </Box>
    );
  };

  const vazio = busca
    ? 'Nada encontrado para a busca'
    : 'Nenhum Nível ou Conta cadastrado na DFC';

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Plano de Contas (DFC)"
        subtitle="Níveis e Contas que classificam o Fluxo de Caixa"
        actions={
          <>
            <Flex ml="auto" my="3" align="center" gridGap={2} flexWrap="wrap">
              <OktonSearchFilter
                value={busca}
                onChange={setBusca}
                placeholder="Buscar por item, nome ou conta..."
              />
              <Flex
                borderWidth="1px"
                p="2"
                rounded="2xl"
                bg={bg}
                align="center"
                gridGap={2}
              >
                <Button
                  leftIcon={
                    tudoRecolhido ? (
                      <ChevronsDownUp size={18} />
                    ) : (
                      <ChevronsUpDown size={18} />
                    )
                  }
                  variant="outline"
                  colorScheme="blue"
                  borderWidth="2px"
                  onClick={() =>
                    tudoRecolhido ? setFechados({}) : recolherTudo()
                  }
                  isDisabled={!!busca}
                >
                  {tudoRecolhido ? 'Expandir tudo' : 'Recolher tudo'}
                </Button>
                <Button
                  leftIcon={<Plus size={18} />}
                  colorScheme="blue"
                  onClick={() => history.push('/plano-contas/novo')}
                  minW="150px"
                >
                  Novo Registro
                </Button>
              </Flex>
            </Flex>
          </>
        }
      />

      {isLoading ? (
        <Flex mt="1">
          <OktonLoadingCard h="200px" />
        </Flex>
      ) : isError ? (
        <Flex mt="1">
          <OktonErrorCard h="200px" onRetry={refetch} />
        </Flex>
      ) : (
        <Box
          bg={bg}
          mt="1"
          mb="6"
          borderWidth="1px"
          rounded="2xl"
          overflowX="auto"
        >
          {arvore.length === 0 || (visiveis && visiveis.size === 0) ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py="20"
              color="gray.400"
              gridGap="2"
            >
              <Network size={48} color="#e2e8f0" />
              <Text fontSize="sm">{vazio}</Text>
            </Flex>
          ) : (
            <Box>{arvore.map(desenhaNo)}</Box>
          )}
        </Box>
      )}
      <OktonConfirmDialog
        isOpen={Boolean(itemExcluir)}
        onClose={() => {
          if (!excluindo) setItemExcluir(null);
        }}
        onConfirm={excluir}
        title="Confirmar exclusão"
        description={
          itemExcluir?.conta_pct
            ? 'Excluir esta Conta do Plano de Contas?'
            : 'Excluir este Nível do Plano de Contas?'
        }
        confirmLabel="Excluir"
        intent="danger"
        isLoading={Boolean(excluindo)}
      />
    </BankAppPage>
  );
};

export default PlanoContas;
